import { createRef } from "react";
import { Button, Spinner } from "react-bootstrap";
import NumberFormat from "react-number-format";
import ChangeHighlight from "react-change-highlight";
import BlockyHeavyText from "components/custom-texts/blocky-heavy-text";
import BlockyBoldText from "components/custom-texts/blocky-bold-text";
import { secondaryColor } from "utils/constants";
import styles from "assets/shared-stylesheets/sparket-list-item.module.css";
import highlightStyles from "components/pool-details/bet-modal/bet-modal-content/styles.module.css";
import BackCardShell from "./BackCardShell";
import { BackCardProps } from "./types";
import { CardHeader, WinConditionRow, Disclaimer } from "./SharedParts";
import popStyles from "./pop.module.css";

/**
 * Team review iteration 1.
 * Built on top of VariantCombined (bold values + pop-on-flip).
 * Picks/balance block uses the centered, label-on-top layout from
 * VariantCenteredBalance for both basic and pro.
 * "Picks Remaining" renamed to "Picks Left".
 */
const VariantTeamPickV1 = (props: BackCardProps) => {
  const {
    eventName,
    poolName,
    winCondition,
    contestantName,
    isBasic,
    picksRemaining,
    expReturn,
    balanceLabel,
    balanceValue,
    oddsLabel,
    betAmount,
    onBetAmountChange,
    onConfirm,
    onCancel,
    loading,
    disabled,
  } = props;

  const valueStyle = {
    color: "var(--text-color)",
    fontWeight: 800 as const,
  };

  return (
    <BackCardShell>
      <CardHeader
        eventName={eventName}
        poolName={poolName}
        onCancel={onCancel}
      />
      <WinConditionRow winCondition={winCondition} />

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <BlockyHeavyText
            className={popStyles.pop}
            style={{
              fontSize: 16,
              ...valueStyle,
              wordBreak: "break-word",
            }}
          >
            {contestantName}
          </BlockyHeavyText>
        </div>
        {isBasic ? (
          // Basic: inline label + value layout. Picks number kept at the
          // same size as the pro balance number for visual consistency.
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <BlockyHeavyText className={styles.poolCloseDatePrefix}>
              Picks Left:
            </BlockyHeavyText>
            <BlockyBoldText
              className={popStyles.pop}
              style={{ fontSize: 16, ...valueStyle, lineHeight: 1 }}
            >
              {picksRemaining ?? 0}
            </BlockyBoldText>
          </div>
        ) : (
          // Pro: balance label stacked above the value, justified center.
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 110,
            }}
          >
            <BlockyHeavyText
              style={{
                display: "block",
                color: "gray",
                fontSize: 10,
                textAlign: "center",
              }}
            >
              {balanceLabel || "Balance"}
            </BlockyHeavyText>
            <BlockyBoldText
              className={popStyles.pop}
              style={{
                fontSize: 16,
                ...valueStyle,
                lineHeight: 1.1,
                textAlign: "center",
              }}
            >
              {balanceValue}
            </BlockyBoldText>
          </div>
        )}
      </div>

      <div>
        {isBasic ? (
          <div style={{ padding: 3 }}>
            <Button
              style={{
                width: "100%",
                height: 41,
                backgroundColor: secondaryColor,
                color: "black",
              }}
              disabled={loading || disabled}
              onClick={onConfirm}
            >
              {loading && (
                <Spinner
                  animation="border"
                  size="sm"
                  style={{ marginRight: 10 }}
                />
              )}
              Confirm
            </Button>
            <div
              style={{
                marginTop: 7,
                display: "flex",
                alignItems: "flex-end",
              }}
            >
              <BlockyBoldText>Exp. Return: &nbsp;</BlockyBoldText>
              <BlockyBoldText>
                <ChangeHighlight
                  hideAfter={1000}
                  highlightClassName={highlightStyles.highlight}
                >
                  <span
                    ref={createRef()}
                    className={popStyles.pop}
                    style={{ ...valueStyle, fontSize: 16 }}
                  >
                    {expReturn}
                  </span>
                </ChangeHighlight>
              </BlockyBoldText>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", padding: 3 }}>
            <div style={{ flex: 0.5 }}>
              <NumberFormat
                value={betAmount}
                onValueChange={(v) => onBetAmountChange?.(v.formattedValue)}
                prefix="§"
                decimalScale={2}
                thousandSeparator
                inputMode="decimal"
                fixedDecimalScale
                allowNegative={false}
                placeholder="Enter Amount"
                style={{
                  borderRadius: 5,
                  height: 40,
                  color: "black",
                  width: "95%",
                  fontSize: 16,
                }}
              />
              <div style={{ display: "flex" }}>
                <BlockyBoldText
                  style={{
                    marginTop: 7,
                    display: "flex",
                    alignItems: "flex-end",
                  }}
                >
                  Odds: &nbsp;
                  <ChangeHighlight
                    hideAfter={1000}
                    highlightClassName={highlightStyles.highlight}
                  >
                    <div
                      ref={createRef()}
                      className={popStyles.pop}
                      style={{ ...valueStyle, fontSize: 16 }}
                    >
                      {oddsLabel}
                    </div>
                  </ChangeHighlight>
                </BlockyBoldText>
              </div>
            </div>
            <div style={{ width: "100%", flex: 0.5 }}>
              <Button
                style={{
                  width: "100%",
                  height: 41,
                  backgroundColor: secondaryColor,
                  color: "black",
                }}
                disabled={loading || disabled}
                onClick={onConfirm}
              >
                {loading && (
                  <Spinner
                    animation="border"
                    size="sm"
                    style={{ marginRight: 10 }}
                  />
                )}
                Confirm
              </Button>
              <div style={{ marginTop: 7 }}>
                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <BlockyBoldText>Exp. Return: &nbsp;</BlockyBoldText>
                  <BlockyBoldText>
                    <ChangeHighlight
                      hideAfter={1000}
                      highlightClassName={highlightStyles.highlight}
                    >
                      <span
                        ref={createRef()}
                        className={popStyles.pop}
                        style={{ ...valueStyle, fontSize: 16 }}
                      >
                        {expReturn}
                      </span>
                    </ChangeHighlight>
                  </BlockyBoldText>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <Disclaimer />
    </BackCardShell>
  );
};

export default VariantTeamPickV1;
